import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { prefersReducedMotion } from "../../RoyalMahal/hooks";
import { UTSAV_THEME } from "../theme.config";
import { coupleNames } from "../helpers";
import { GATE_VARS, GateFrame, GateLeaf } from "../art/Gate";
import { between, createRng, round } from "../art/shapes";
import SacredMark from "./SacredMark";

// The doors swing open, haldi light floods out, then the gate dissolves into the card.
const DOORS_MS = 1700;
const LIGHT_MS = 650;
const FADE_MS = 800;

// Marigold, haldi and rani petals, scattered from the knob the moment it is tapped.
const PETAL_COLORS = ["#ec7a0c", "#f79a17", "#ffcd2e", "#d81b60"];
// Placed from a seed, so the spray is the same every time. Distances are fractions of the gate's height.
const PETALS = (() => {
  const rng = createRng(37);
  return Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * Math.PI * 2 + between(rng, -0.2, 0.2);
    const distance = between(rng, 0.14, 0.3);
    return {
      color: PETAL_COLORS[i % PETAL_COLORS.length],
      dx: round(Math.cos(angle) * distance),
      dy: round(Math.sin(angle) * distance - 0.04),
      rot: Math.round(between(rng, -300, 300)),
    };
  });
})();

const PetalSpray = () => (
  <div className="ut-gate__spray" aria-hidden="true">
    {PETALS.map((p, i) => (
      <span key={i} className="ut-gate__petal" style={{ background: p.color, "--fx": p.dx, "--fy": p.dy, "--rot": `${p.rot}deg` }} />
    ))}
  </div>
);

/**
 * First screen of the invitation, kept quiet: the welcome and the couple's names over a single
 * Jaipur-pink arch with a genda phool mala, its peacock-teal doors shut. Tapping the marigold
 * knob scatters a few petals and swings the doors open; haldi light floods the screen and the
 * card is revealed beneath.
 */
const GateScreen = ({ bride, groom, onReveal, onDone }) => {
  const [stage, setStage] = useState("closed");
  const timers = useRef([]);
  const { first, second } = coupleNames(bride, groom);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const open = () => {
    if (stage !== "closed") return;
    // Start the music inside the tap itself so mobile browsers allow it.
    const music = document.getElementById("backgroundMusic");
    music?.play?.()?.catch?.(() => {});

    if (prefersReducedMotion()) {
      onReveal();
      setStage("leaving");
      later(onDone, FADE_MS);
      return;
    }
    setStage("opening");
    later(() => setStage("entering"), DOORS_MS);
    later(() => {
      onReveal();
      setStage("leaving");
    }, DOORS_MS + LIGHT_MS);
    later(onDone, DOORS_MS + LIGHT_MS + FADE_MS);
  };

  return (
    <div className={`ut-gate is-${stage}`} role="dialog" aria-modal="true" aria-label="Wedding invitation">
      <div className="ut-gate__intro">
        <SacredMark className="ut-hero__emblem" />
        <p className="ut-gate__welcome" aria-label={UTSAV_THEME.gateTitleLabel}>
          <span aria-hidden="true">{UTSAV_THEME.gateTitle}</span>
        </p>
        <p className="ut-gate__names">
          {first} <span className="ut-script">&amp;</span> {second}
        </p>
      </div>

      <div className="ut-gate__doorway" style={GATE_VARS}>
        <div className="ut-gate__glow" aria-hidden="true" />
        <div className="ut-gate__leaves" aria-hidden="true">
          <div className="ut-gate__leaf ut-gate__leaf--left">
            <GateLeaf side="left" />
          </div>
          <div className="ut-gate__leaf ut-gate__leaf--right">
            <GateLeaf side="right" />
          </div>
        </div>
        <GateFrame className="ut-gate__frame" />
        <PetalSpray />
        {/* The handle: a marigold at the seam where the two doors meet */}
        <button type="button" className="ut-open-btn" onClick={open} disabled={stage !== "closed"} aria-label="Open Invitation">
          <svg className="ut-open-btn__flower" viewBox="-11 -11 22 22" aria-hidden="true">
            <use href="#ut-marigold" x="-11" y="-11" width="22" height="22" />
          </svg>
          <span className="ut-open-btn__face">Open</span>
          <span className="ut-open-btn__hint" aria-hidden="true">
            Tap to open
          </span>
        </button>
      </div>

      <div className="ut-gate__light" aria-hidden="true" />
    </div>
  );
};

const mapStateToProps = (state) => ({
  bride: state.inviteDetails.invite?.bride,
  groom: state.inviteDetails.invite?.groom,
});

export default connect(mapStateToProps)(GateScreen);
